import {
  PermissionCode,
  type FleetDamageReport,
  type UpdateFleetDamageStatusRequest,
} from '@gb-ticket/shared-types';
import { Link, Navigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  FLEET_DAMAGE_KIND_LABELS,
  FLEET_DAMAGE_SEVERITY_LABELS,
  FLEET_DAMAGE_STATUS_LABELS,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import { fleetTableFrameClassName } from '@/products/fleet/features/fleet-panel';
import {
  useFleetDamagesAll,
  useUpdateFleetDamageStatus,
} from '@/products/fleet/features/use-fleet';
import { FLEET_ROOT, fleetVehiclePath } from '@/products/fleet/fleet-paths';

const NEXT_STATUS: Record<
  FleetDamageReport['status'],
  UpdateFleetDamageStatusRequest['status'][] | null
> = {
  reported: ['triaged', 'rejected'],
  triaged: ['in_workshop', 'done', 'rejected'],
  in_workshop: ['done', 'rejected'],
  done: null,
  rejected: null,
};

export function FleetReportsPage() {
  usePageTitle('Meldungen');
  const me = useMe();
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const damages = useFleetDamagesAll(manage);
  const updateStatus = useUpdateFleetDamageStatus();
  const [search] = useSearchParams();
  const focusId = search.get('meldung');

  if (me.data && !manage) {
    return <Navigate to={FLEET_ROOT} replace />;
  }

  async function setStatus(id: string, status: UpdateFleetDamageStatusRequest['status']) {
    try {
      await updateStatus.mutateAsync({ id, body: { status } });
      toast.success('Status gespeichert.');
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Status konnte nicht gespeichert werden.';
      toast.error(message);
    }
  }

  const items = damages.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Meldungen"
        description="Offene und erledigte Schadenmeldungen aller Fahrzeuge."
      />
      {damages.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {damages.isError ? (
        <p className="text-sm text-destructive">
          {damages.error instanceof ApiRequestError
            ? damages.error.message
            : 'Meldungen konnten nicht geladen werden.'}
        </p>
      ) : null}
      {items.length === 0 && !damages.isPending ? (
        <p className="text-sm text-muted-foreground">Keine Meldungen.</p>
      ) : null}
      {items.length > 0 ? (
        <>
          <div className={`hidden md:block ${fleetTableFrameClassName}`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Kennzeichen</TableHead>
                  <TableHead>Art</TableHead>
                  <TableHead>Fahrbereitschaft</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const next = NEXT_STATUS[row.status];
                  return (
                    <TableRow
                      key={row.id}
                      className={row.id === focusId ? 'bg-muted/50' : undefined}
                    >
                      <TableCell>{FLEET_DAMAGE_STATUS_LABELS[row.status]}</TableCell>
                      <TableCell>
                        <Link
                          to={fleetVehiclePath(row.vehicleId)}
                          className="font-medium text-primary hover:underline"
                        >
                          {row.licensePlate ?? '—'}
                        </Link>
                      </TableCell>
                      <TableCell>{FLEET_DAMAGE_KIND_LABELS[row.kind]}</TableCell>
                      <TableCell>{FLEET_DAMAGE_SEVERITY_LABELS[row.severity]}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDeDateTime(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        {next && next.length > 0 ? (
                          <div className="flex flex-wrap justify-end gap-1">
                            {next.map((status) => (
                              <Button
                                key={status}
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={updateStatus.isPending}
                                onClick={() => setStatus(row.id, status)}
                              >
                                {FLEET_DAMAGE_STATUS_LABELS[status]}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
            {items.map((row) => {
              const next = NEXT_STATUS[row.status];
              return (
                <li
                  key={row.id}
                  className={`space-y-2 p-3 ${row.id === focusId ? 'bg-muted/50' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={fleetVehiclePath(row.vehicleId)}
                      className="text-lg font-semibold text-primary hover:underline"
                    >
                      {row.licensePlate ?? '—'}
                    </Link>
                    <span className="text-sm">{FLEET_DAMAGE_STATUS_LABELS[row.status]}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {FLEET_DAMAGE_KIND_LABELS[row.kind]} ·{' '}
                    {FLEET_DAMAGE_SEVERITY_LABELS[row.severity]}
                  </p>
                  <p className="text-sm text-muted-foreground">{formatDeDateTime(row.createdAt)}</p>
                  {next && next.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {next.map((status) => (
                        <Button
                          key={status}
                          type="button"
                          size="sm"
                          variant="outline"
                          className="min-h-11"
                          disabled={updateStatus.isPending}
                          onClick={() => setStatus(row.id, status)}
                        >
                          {FLEET_DAMAGE_STATUS_LABELS[status]}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}
